import type { ComponentInterface, EventEmitter } from '@stencil/core';
import type { Color } from '../../interface';
import type { OverlayEventDetail } from './interface';
import { Component, Host, h, Prop, Watch, Element, Method, Event } from '@stencil/core';
import { createTriggerController } from "../../utils/overlays"
@Component({
  tag: 'jackery-modal',
  styleUrl: 'jackery-modal.css',
  shadow: true,
})                            

export class JackeryModal implements ComponentInterface {
  private readonly triggerController = createTriggerController();
  private okEl?: HTMLElement;
  private cancelEl?: HTMLElement;
  @Element() el!: HTMLJackeryModalElement;
  /**
   * 是否显示
   */
  @Prop({mutable: true}) open = false;
  @Watch('open')
  onIsOpenChange(newValue: boolean, oldValue: boolean) {
    if (newValue === true && oldValue === false) {
      this.modalOpen.emit()
    } else if (newValue === false && oldValue === true) {
      this.modalClose.emit();
    }
  }

  /**
   * 弹窗内容容器尺寸：
   * `"sm"`: PC: 480px. MB: 74.6VW
   * `"md"`: PC: 660px. MB: 85.3VW
   * `"lg"`: PC: 880px. MB: 100VW
   */
  @Prop() size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * 遮罩层级
   */
  @Prop() zindex: number = 999;

  /**
   * 是否允许点击遮罩触发关闭
   */
  @Prop() modalcloseable: boolean = true;

  /**
   * 是否显示右上角关闭按钮
   */
  @Prop() closeable: boolean = true;

  /**
   * 弹窗标题
   */
  @Prop() header?: string;

  /**
   * 弹窗副标题
   */
  @Prop() subHeader?: string;

  /**
   * 文案对齐方式
   */
  @Prop() textAlign: 'left' | 'center' | 'right' = 'left';

  /**
   * 确认按钮文案
   */
  @Prop() oktext?: string;

  /**
   * 确认callback函数
   * oktext存在时候有效
   */
  @Prop() ok?: ((data?: any) => Promise<boolean>);

  /**
   * 取消callback函数
   * canceltext存在时候有效
   */
  @Prop() cancel?: ((data?: any) => Promise<boolean>);

  /**
   * 确认按钮颜色风格
   * oktext存在时候有效
   */
  @Prop() okcolor?: Color;

  /**
   * 取消按钮文案
   * canceltext存在时候有效
   */
  @Prop() canceltext?: string;

  /**
   * 取消按钮颜色风格
   * oktext/canceltext存在时候有效
   */
  @Prop() cancelcolor?: Color;

  /**
   * footer按钮尺寸
   * oktext/canceltext存在时候有效
   */
   @Prop() btnsize?: 'sm' | 'md' | 'lg' | 'xl' = 'md';

  /** @internal */
  @Prop() overlayIndex!: number;

  /**
   * 触发元素的ID
   * 点击触发元素时打开modal
   */
  @Prop() trigger: string | undefined;
  @Watch('trigger')
  triggerChanged() {
    const { trigger, triggerController } = this;
    if (trigger) {
      triggerController.addClickListener(this.el, trigger);
    }
  }

  /**
   * 触发元素的ID
   * 点击触发元素时关闭modal
   */
  @Prop() triggerclose: string | undefined;
  @Watch('triggerclose')
  triggercloseChanged() {
    const { triggerclose, triggerController } = this;
    if (triggerclose) {
      triggerController.addClickCloseListener(this.el, triggerclose);
    }
  }

  connectedCallback() {
    this.triggerChanged();
    this.triggercloseChanged();
  }

  private renderCloseIcon = () => {
    return (
      <g id="弹窗验收" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="弹窗更新" transform="translate(-641.000000, -346.000000)" fill="#BCC4CC" fill-rule="nonzero"><g id="编组" transform="translate(350.000000, 334.000000)"><g id="图形类/01_图标/16x16/03_关闭_black备份-2" transform="translate(291.000000, 12.000000)"><path d="M2.28033009,1.21966991 L7.937,6.876 L13.5940386,1.21966991 C13.8869318,0.926776695 14.3618055,0.926776695 14.6546988,1.21966991 C14.947592,1.51256313 14.947592,1.98743687 14.6546988,2.28033009 L8.997,7.937 L14.6546988,13.5940386 C14.947592,13.8869318 14.947592,14.3618055 14.6546988,14.6546988 C14.3618055,14.947592 13.8869318,14.947592 13.5940386,14.6546988 L7.937,8.997 L2.28033009,14.6546988 C1.98743687,14.947592 1.51256313,14.947592 1.21966991,14.6546988 C0.926776695,14.3618055 0.926776695,13.8869318 1.21966991,13.5940386 L6.876,7.937 L1.21966991,2.28033009 C0.926776695,1.98743687 0.926776695,1.51256313 1.21966991,1.21966991 C1.51256313,0.926776695 1.98743687,0.926776695 2.28033009,1.21966991 Z" id="形状结合" transform="translate(7.937184, 7.937184) rotate(-360.000000) translate(-7.937184, -7.937184) "></path></g></g></g></g>
    )
  }

  @Method()
  async hide () {
    this.open = false
  }

  @Method()
  async show() {
    this.open = true
  }

  /**
   * modal关闭调用
   */
  @Event({ eventName: 'jkyModalClose' }) modalClose!:EventEmitter<OverlayEventDetail>;

  /**
   * modal开启调用
   */
  @Event({ eventName: 'jkyModalOpen' }) modalOpen!:EventEmitter<OverlayEventDetail>;

  private handleOK = async (ev: Event) => {
    const { ok } = this;
    if(typeof ok === 'function') {
      this.okEl.setAttribute("loading", "true")
      await ok(ev)?.catch(e => {
        console.error(`handleOK: ${e}`)
        this.hide()
      })
      this.hide()
      this.okEl.removeAttribute("loading")
    } else {
      this.hide()
    }
  };

  private handleCancel = async (ev: Event) => {
    const { cancel } = this;
    if(typeof cancel === 'function') {
      this.cancelEl.setAttribute("loading", "true")
      await cancel(ev)?.catch(e => {
        console.error(`handleCancel: ${e}`)
        this.hide()
      })
      this.hide()
      this.cancelEl.removeAttribute("loading")
    } else {
      this.hide()
    }
  }


  render() {
    const {zindex, size, modalcloseable, closeable, header, subHeader, textAlign, renderCloseIcon, oktext, canceltext, okcolor, cancelcolor, btnsize} = this
    const path = renderCloseIcon();

    return (
      <Host
        style={{
          "--text-align": textAlign,
        }}
      >
        <jackery-overlay 
          zindex={zindex} 
          size={size} 
          open={this.open}
          closeable={modalcloseable}
          onJkyClose={() => this.open = false}
        >
          <div class="modal-content">
            {
              closeable && <svg onClick={() => this.open = false} class="close-icon" viewBox="0 0 16 16" version="1.1" xmlns="http://www.w3.org/2000/svg" part="svg">{path}</svg>
            }

            {
              header && (
                <h2 class="modal-title">{header}</h2>
              )
            }

            {
              subHeader && (
                <h3 class="modal-sub-title">{subHeader}</h3>
              )
            }

            <slot name="header"></slot>
            <slot />
            <slot name="footer"></slot>

            {
              (canceltext || oktext) && (
                <div class="footer-btns">
                  {
                    canceltext && <div class="footer--btn"><jackery-button size={btnsize} full={true} color={cancelcolor} onClick={(e) => this.handleCancel(e)} ref={(cancelEl) => (this.cancelEl = cancelEl)}>{canceltext}</jackery-button></div>
                  }

                  {
                    oktext && <div class="footer--btn"><jackery-button ref={(okEl) => (this.okEl = okEl)} size={btnsize} full={true} color={okcolor} onClick={(e) => this.handleOK(e)}>{oktext}</jackery-button></div>
                  }
                </div>
              )
            }
          </div>
        </jackery-overlay>
        <slot></slot>
      </Host>
    );
  }
}